import { Component, OnInit } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { FormControl, FormGroup } from '@angular/forms';
import { ChainsService } from 'src/app/services/chains.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { ToastrService } from 'ngx-toastr';
import Swal from 'sweetalert2';

@Component({
  selector: 'app-chain-branding',
  templateUrl: './chain-branding.component.html',
  styleUrls: ['./chain-branding.component.scss']
})
export class ChainBrandingComponent implements OnInit {
  chainId: string = '';

  fontOptions: string[] = [
    'Roboto',
    'Open Sans',
    'Lato',
    'Montserrat',
    'Poppins',
    'Inter',
    'Raleway'
  ];

  brandingForm: any;

  logoFile: File | null = null;
  bannerFile: File | null = null;
  faviconFile: File | null = null;
  logoPreview: string | null = null;
  bannerPreview: string | null = null;
  faviconPreview: string | null = null;

  constructor(
    private route: ActivatedRoute,
    public chainsService: ChainsService,
    private spinner: NgxSpinnerService,
    private toastr: ToastrService
  ) {}

  ngOnInit(): void {
    this.chainId = this.route.snapshot.params['chainId'] || '';

    this.brandingForm = new FormGroup({
      primaryColor: new FormControl('#3366FF'),
      secondaryColor: new FormControl('#FF6B35'),
      accentColor: new FormControl('#00D68F'),
      backgroundColor: new FormControl('#F7F9FC'),
      fontFamily: new FormControl('Roboto'),
      logoUrl: new FormControl(''),
      bannerImageUrl: new FormControl(''),
      faviconUrl: new FormControl('')
    });

    if (this.chainId) {
      this.loadBranding();
    }
  }

  loadBranding(): void {
    this.spinner.show();
    this.chainsService.getChainBranding(this.chainId).subscribe({
      next: (res: any) => {
        const branding = res.branding || res.response?.data?.branding || res.response?.data || {};
        if (branding) {
          this.brandingForm.patchValue({
            primaryColor: branding.primaryColor || '#3366FF',
            secondaryColor: branding.secondaryColor || '#FF6B35',
            accentColor: branding.accentColor || '#00D68F',
            backgroundColor: branding.backgroundColor || '#F7F9FC',
            fontFamily: branding.fontFamily || 'Roboto',
            logoUrl: branding.logoUrl || '',
            bannerImageUrl: branding.bannerImageUrl || '',
            faviconUrl: branding.faviconUrl || ''
          });
        }
        this.spinner.hide();
      },
      error: (err: any) => {
        this.spinner.hide();
        if (err.status !== 404) {
          Swal.fire('Error', err.error?.response?.message || 'Failed to load branding settings', 'error');
        }
      }
    });
  }

  onFileSelect(event: Event, field: 'logo' | 'banner' | 'favicon'): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const reader = new FileReader();
    reader.onload = (e) => {
      const preview = e.target?.result as string;
      if (field === 'logo') { this.logoFile = file; this.logoPreview = preview; }
      if (field === 'banner') { this.bannerFile = file; this.bannerPreview = preview; }
      if (field === 'favicon') { this.faviconFile = file; this.faviconPreview = preview; }
    };
    reader.readAsDataURL(file);
  }

  onSave(): void {
    const hasFiles = this.logoFile || this.bannerFile || this.faviconFile;

    const saveWithUrls = (urlOverrides: any = {}) => {
      const payload = {
        primaryColor: this.brandingForm.get('primaryColor').value,
        secondaryColor: this.brandingForm.get('secondaryColor').value,
        accentColor: this.brandingForm.get('accentColor').value,
        backgroundColor: this.brandingForm.get('backgroundColor').value,
        fontFamily: this.brandingForm.get('fontFamily').value,
        logoUrl: this.brandingForm.get('logoUrl').value,
        bannerImageUrl: this.brandingForm.get('bannerImageUrl').value,
        faviconUrl: this.brandingForm.get('faviconUrl').value,
        ...urlOverrides
      };

      this.chainsService.updateChainBranding(this.chainId, payload).subscribe({
        next: (res: any) => {
          this.brandingForm.patchValue(urlOverrides);
          this.spinner.hide();
          this.toastr.success('Branding saved successfully');
          this.logoFile = this.bannerFile = this.faviconFile = null;
          this.logoPreview = this.bannerPreview = this.faviconPreview = null;
        },
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', err.error?.response?.message || 'Failed to save branding', 'error');
        }
      });
    };

    this.spinner.show();

    if (hasFiles) {
      const fd = new FormData();
      if (this.logoFile) fd.append('logo', this.logoFile);
      if (this.bannerFile) fd.append('banner', this.bannerFile);
      if (this.faviconFile) fd.append('favicon', this.faviconFile);

      this.chainsService.uploadChainFiles(this.chainId, fd).subscribe({
        next: (res: any) => saveWithUrls(res?.response?.data || res.data || {}),
        error: (err: any) => {
          this.spinner.hide();
          Swal.fire('Error', 'File upload failed', 'error');
        }
      });
    } else {
      saveWithUrls();
    }
  }

  get primaryColor(): string {
    return this.brandingForm?.get('primaryColor')?.value || '#3366FF';
  }

  get secondaryColor(): string {
    return this.brandingForm?.get('secondaryColor')?.value || '#FF6B35';
  }

  get accentColor(): string {
    return this.brandingForm?.get('accentColor')?.value || '#00D68F';
  }

  get backgroundColor(): string {
    return this.brandingForm?.get('backgroundColor')?.value || '#F7F9FC';
  }

  get fontFamily(): string {
    return this.brandingForm?.get('fontFamily')?.value || 'Roboto';
  }

  get logoUrl(): string {
    return this.brandingForm?.get('logoUrl')?.value || '';
  }
}
